import AgentModel from "@/components/atoms/agentModel/AgentModel";
import AvatarBase from "@/components/atoms/avatarBase/AvatarBase";
import ChipCategory from "@/components/atoms/chipCategory/ChipCategory";
import FolderLineIcon from "@/components/atoms/svg/icons/FolderLineIcon";
import HeartIcon from "@/components/atoms/svg/icons/HeartIcon";
import { CardDescription, CardTitle } from "@/components/ui/card";
import { USER } from "@/constants/mock";
import { IAgentData } from "@/interfaces/IAgentData";
import EMPTY_IMAGE from "@/public/share_thumbnail.webp";
import clsx from "clsx";
import { StaticImport } from "next/dist/shared/lib/get-img-props";
import Image from "next/image";
import Link from "next/link";
import { useMemo, useState } from "react";

type IAgentCardProps = Pick<
  IAgentData,
  | "image"
  | "description"
  | "name"
  | "ai_provider"
  | "ai_model"
  | "_id"
  | "tags"
  | "avatar_img"
  | "variables"
  | "shared_amount"
  | "favorite_amount"
  | "user_id"
  | "image_model_ai"
  | "remixed_amount"
>;

export default function AgentCard(props: IAgentCardProps) {
  const [image, setImage] = useState<string | StaticImport>(EMPTY_IMAGE.src);

  useMemo(() => {
    if (props.image && typeof props.image === "string") {
      setImage(props.image);
    }
  }, [props.image]);
  return (
    <div
      className={clsx(
        "flex-1 overflow-hidden flex flex-col gap-2 w-full xl:w-[282px]",
      )}
    >
      <Link
        href={`/view/${props._id}` || "#"}
        className={clsx("block cursor-pointer")}
        aria-label={`Agent profile for ${props.name}`}
      >
        <div
          className={clsx(
            "card-image relative rounded-[8px] overflow-hidden border border-base-divider",
          )}
        >
          <Image
            src={image}
            onError={() => setImage("/fallback-image.jpg")} // Change to fallback image
            alt={props.name}
            width={EMPTY_IMAGE.width / 2}
            height={EMPTY_IMAGE.height / 2}
            className="object-cover aspect-square rounded-[8px] overflow-hidden"
          />
          <div className="absolute bottom-0 left-0 w-full p-2 h-14 bg-gradient-05">
            <div className="relative flex gap-4 items-center w-full h-full">
              <div
                className={clsx(
                  "flex gap-3 justify-center items-center w-auto h-auto",
                )}
              >
                <FolderLineIcon />
                <p className="text-white font-medium text-xs">
                  {props.remixed_amount}
                </p>
              </div>
              <div className={clsx("w-px h-3 bg-white/30 mx-1")} />
              <div
                className={clsx(
                  "flex gap-2 justify-center items-center w-auto h-auto",
                )}
              >
                <HeartIcon fill="white" />
                <p className="text-white font-medium text-xs">
                  {props.favorite_amount}
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="card-content px-2 py-4 flex flex-col items-start gap-4">
          <div className="flex flex-col gap-[8px]">
            <CardTitle
              className={clsx(
                "text-sm line-clamp-1 text-base-primary font-medium leading-caption-l tracking-caption-l m-0",
              )}
            >
              {props.name}
            </CardTitle>
            <CardDescription
              className={clsx(
                "line-clamp-2 text-base-secondary/60 font-normal text-sm leading-caption-l tracking-caption-l min-h-10 break-all",
              )}
            >
              {props.description}
            </CardDescription>
            <div
              className={clsx(
                "flex justify-start items-center w-full h-auto gap-1 sm:gap-2 text-base-tertiary text-[12px] leading-caption-m font-medium flex-wrap",
              )}
            >
              <div className={clsx("flex gap-2")}>
                <AvatarBase
                  avatar_img={props.user_id?.avatar_img ?? USER.avatar_img}
                  className="h-5 w-5 overflow-hidden rounded-full"
                />
                {/* Todo wait for BE about user_id for agent card */}
                <p className="opacity-40">
                  {props.user_id?.username ?? USER.username}
                </p>
              </div>
              <div className={clsx("w-[1px] h-2 bg-base-divider")} />
              <AgentModel
                ai_model={props.ai_model}
                image_model_ai={props.image_model_ai}
              />
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {props.tags?.map((_category, index) => (
              <ChipCategory
                key={`tag-${index}`}
                {..._category}
                className="!h-[24px] whitespace-nowrap !text-xs"
                width={14}
                height={14}
                textStyle="!text-xs"
              />
            ))}
          </div>
        </div>
      </Link>
    </div>
  );
}
